<template>
  <v-timeline-item
    v-if="node"
    :icon="nodeIcon"
    :color="nodeColor"
    icon-color="primary"
    fill-dot
    class="ma-0 pa-0"
    small
  >
    <v-row
      class="ma-0 pa-0 pr-4"
      :id="`algorithm-${level}`"
      :ref="`algorithm-${level}`"
    >
      <v-col v-if="node.type == 1" cols="12" class="ma-0 pa-0">
        <vetalfa-sections
          :modifySectionItemTitle="true"
          :sections="node.sections"
        ></vetalfa-sections>
        <v-spacer></v-spacer>
      </v-col>
      <v-col v-else-if="node.type == 2" cols="12" class="ma-0 pa-0">
        <v-row
          class="primary--text pl-2 ma-0 pa-0"
          justify="start"
          align="center"
        >
          <span class="text-start" v-html="node.question"></span>
        </v-row>

        <v-list dense class="transparent">
          <v-list-item-group
            active-class="algorithm-node-option-active"
            v-model="selected"
          >
            <div v-for="(option, i) in node.options" :key="i">
              <v-list-item class="mb-1">
                <template v-slot:default="{ active }">
                  <v-list-item-action class="mr-2 pa-0">
                    <v-checkbox :input-value="active"></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content class="ma-0 pa-0">
                    <v-list-item-title
                      style="white-space: normal !important"
                      v-html="option.title"
                    ></v-list-item-title>
                    <v-list-item-subtitle v-if="option.subtitle">
                      {{ option.subtitle }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-list-item>
              <v-divider
                v-if="i + 1 < node.options.length"
                class="mb-1"
              ></v-divider>
            </div>
          </v-list-item-group>
        </v-list>
      </v-col>
      <v-col v-else-if="node.type == 5" cols="12" class="ma-0 pa-0">
        <slot :name="node.slot"></slot>
      </v-col>
      <v-col v-else cols="12" class="ma-0 pa-0">
        <v-form v-model="validDataForm" @submit.prevent>
          <v-row
            class="primary--text pl-2 ma-0 pa-0"
            justify="start"
            align="center"
          >
            <span class="text-start" v-html="node.question"></span>
          </v-row>
          <v-row
            class="primary--text pl-2 ma-0 pa-0"
            justify="center"
            align="center"
          >
            <v-col
              v-for="{
                kind,
                model,
                label,
                rule,
                length,
                items,
                mask,
                placeholder,
              } in node.inputs"
              :key="model"
              cols="12"
              class="ma-0 pa-0"
            >
              <v-text-field
                v-if="kind === 'textfield' && !!mask"
                light
                dense
                color="primary"
                outlined
                :label="label"
                :placeholder="placeholder"
                v-model="modelBuffer"
                :value="modelBuffer"
                autocapitalize="none"
                :rules="[rule]"
                clearable
                :counter="length"
                v-mask="mask"
                class="mt-4"
              ></v-text-field>
              <v-text-field
                v-else-if="kind === 'textfield' && !mask"
                light
                dense
                color="primary"
                outlined
                :label="label"
                :placeholder="placeholder"
                v-model="modelBuffer"
                autocapitalize="none"
                :rules="[rule]"
                clearable
                :counter="length"
                class="mt-4"
              ></v-text-field>
              <v-autocomplete
                class="mt-4"
                v-else-if="kind === 'select'"
                :items="items"
                :label="label"
                filled
                dense
                outlined
                v-model="modelBuffer"
                :value="modelBuffer"
                :rules="[rule]"
                :menu-props="{
                  maxHeight: _isMobile() ? 200 : 300,
                  top: true,
                  'offset-y': false,
                }"
                @change="autocompleteChangeEventMethod({ node, level })"
              ></v-autocomplete>
              <chip-group
                multiple
                v-else-if="kind === 'chips'"
                v-on:selectChip="selectChip"
                :items="items"
              ></chip-group>
            </v-col>
            <v-col
              v-if="(!nodes[level + 1] && node.type !== 4) || !node.child"
              cols="12"
              class="ma-0 pa-0"
            >
              <v-row class="ma-0 pa-0 mt-4" justify="center">
                <v-btn
                  large
                  class="text-none primary px-8"
                  rounded
                  :disabled="!validDataForm"
                  @click="goToNextNode({ node, level })"
                  >{{ _getLabel("CONTINUE") }}</v-btn
                >
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-timeline-item>
</template>

<style>
.algorithm-node-option-active {
  background-color: rgba(0, 0, 0, 0.01);
}
</style>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { mdiHelpCircle, mdiPencilCircle } from "@mdi/js";
import ChipGroup from "@/components/ChipGroup.vue";
export default {
  name: "algorithm-node",
  props: {
    level: {
      type: Number,
      required: false,
      default: 0,
    },
    headerHeightOffset: {
      type: Number,
      required: false,
      default: 0,
    },
    dialogContainer: {
      required: false,
      default: false,
      type: Boolean,
    },
  },
  components: {
    "chip-group": ChipGroup,
  },
  watch: {
    modelBuffer(val) {
      if (this.node.inputs && this.node.inputs.length > 0) {
        this.setModelBuffer(this.node.inputs[0].model, val);
        const input = this.node.inputs[0];
        if (typeof input.onChange === "function" && !this._suppressOnChange) {
          if (this._onChangeTimer) clearTimeout(this._onChangeTimer);
          this._onChangeTimer = setTimeout(() => {
            const updates = input.onChange(val);
            const apply = (resolved) => {
              if (!resolved || typeof resolved !== "object") return;
              const merged = { ...this.getModels, ...resolved };
              this.setModels(merged);
            };
            if (updates && typeof updates.then === "function") {
              updates.then(apply);
            } else {
              apply(updates);
            }
          }, 400);
        }
      }
    },
    selected(val) {
      if (this.hasSelected) {
        let inputs = { ...this.inputs };
        inputs[this.level] = val;
        if (!this.isMounting) {
          Object.keys(this.inputs).forEach((key) => {
            if (parseInt(key) > this.level) {
              delete inputs[key];
            }
          });
        } else {
          this.isMounting = false;
        }
        if (this.node.model) {
          this.setModelBuffer(this.node.model, val);
        }
        this.setInputs(inputs);
        this.setChildren(this.node.options[val].child);
      } else {
        this.sliceNodesAndInputs();
      }
      setTimeout(
        function () {
          this.formatTimelineHeight();
          this.handleAlgorithmScroll(
            typeof val == "number" ? this.level + 1 : this.level
          );
        }.bind(this),
        100
      );
    },
    validDataForm(val) {
      if (!val) {
        this.sliceNodesAndInputs();

        setTimeout(
          function () {
            this.formatTimelineHeight();
          }.bind(this),
          100
        );
      }
    },
  },
  computed: {
    ...mapGetters("algorithms", ["nodes", "inputs", "getModels"]),
    node() {
      return this.nodes[this.level];
    },
    hasSelected() {
      return this.node.type == 2 && typeof this.selected == "number";
    },
    nodeColor() {
      const isLastNode = this.nodes.length == this.level + 1;
      if (this.node.type == 1) {
        return isLastNode ? "primary" : "rgb(0, 0, 0, 0.14)";
      }
      return "white";
    },
    nodeIcon() {
      return {
        2: this.mdiHelpCircle,
        3: this.mdiPencilCircle,
        4: this.mdiPencilCircle,
      }[this.node.type];
    },
  },
  methods: {
    ...mapActions("algorithms", ["addNode", "sliceNodes", "nextNode"]),
    ...mapMutations("algorithms", ["setInputs", "setModels"]),
    handleAlgorithmScroll(level) {
      if (this.dialogContainer) {
        document
          .getElementById(`algorithm-${level}`)
          .scrollIntoView({ behavior: "smooth" });
      } else {
        this.$vuetify.goTo(`#algorithm-${level}`, {
          duration: 500,
          offset: this.headerHeightOffset,
          easing: "easeInOutCubic",
        });
      }
    },
    setModelBuffer(key, value) {
      if (!key) return;
      this.modelBuffer = value;
      let models = { ...this.getModels };
      models[key] = value;
      this.setModels(models);
    },
    sliceNodesAndInputs() {
      let inputs = { ...this.inputs };
      inputs[this.level] = null;
      if (!this.isMounting) {
        Object.keys(this.inputs).forEach((key) => {
          if (parseInt(key) > this.level) {
            delete inputs[key];
          }
        });
      } else {
        this.isMounting = false;
      }
      this.setInputs(inputs);
      this.sliceNodes(this.level + 1);
    },
    autocompleteChangeEventMethod({ node, level }) {
      if (!!node.child) {
        this.goToNextNode({ node, level });
      }
    },
    goToNextNode({ node, level }) {
      this.nextNode({ node, level });
      setTimeout(
        function () {
          this.formatTimelineHeight();
          this.handleAlgorithmScroll(level + 1);
        }.bind(this),
        100
      );
    },
    selectChip(val) {
      if (!val) {
        this.setModelBuffer(this.node.inputs[0].model, null);
        return;
      }
      this.setModelBuffer(
        this.node.inputs[0].model,
        val.map((v) => v + 1).sort((a, b) => a - b)
      );
    },
    setChildren(child) {
      this.addNode({ level: this.level + 1, ...child });
    },
    formatTimelineHeight() {
      this.$emit("formatTimelineHeight");
    },
    setBufferModelForNodesWithInputs() {
      if (
        this.node.inputs &&
        this.node.inputs.length > 0 &&
        this.node.inputs[0].model &&
        this.getModels[this.node.inputs[0].model] !== undefined &&
        this.getModels[this.node.inputs[0].model] !== null
      ) {
        this._suppressOnChange = true;
        this.setModelBuffer(
          this.node.inputs[0].model,
          this.getModels[this.node.inputs[0].model]
        );
        this.$nextTick(() => {
          this._suppressOnChange = false;
        });
        if (this.node.child) {
          this.goToNextNode({ node: this.node, level: this.level });
        }
      }
    },
  },
  created() {},
  mounted() {
    this.setBufferModelForNodesWithInputs();
    if (this.node && this.node.child && ![3, 4].includes(this.node.type)) {
      this.setChildren(this.node.child);
    } else if (
      typeof this.inputs[this.level] === "number" ||
      typeof this.getModels[this.node.model] === "number"
    ) {
      this.isMounting = true;
      this.$nextTick(() => {
        this.selected =
          typeof this.inputs[this.level] === "number"
            ? this.inputs[this.level]
            : this.getModels[this.node.model];
      });
    }
    this.$nextTick(() => {
      this.formatTimelineHeight();
    });
  },
  data() {
    return {
      isMounting: false,
      selected: null,
      mdiHelpCircle,
      mdiPencilCircle,
      validDataForm: false,
      modelBuffer: null,
    };
  },
};
</script>
